import shortid from "shortid";
import ModificationType from './reference/ModificationType'
import RecognitionRule from './reference/RecognitionRule';
import * as AccountingUtils from './AccountingUtils';
import { Utils, Comparator, moment, Period } from 'revlock-webutils';
import { getStandAlonePrice } from './StandalonePriceHelper';
import * as PSHelper from "./ProfessionalServiceHelper"
import { Codes } from '../../build/core/ErrorCode';
import { selectSSP } from './SSPSelectorHelper';

import Big from "big.js";
import PointInTimePlan from "./plan/PointInTimePlan";
import _ from "lodash";

import { getAutoCreateSSP, prefillZeroAtStart, buildPlan } from './ArrangementUtils'
import * as ExpenseArrangementHelper from "./ExpenseArrangementHelper";

const { formatDate } = Utils;

const defaultSSPForProductCode = (id, attributes) => ({
    attributes: attributes || {},
    commissionRecognitionRuleId: 2,
    deliveryStartDate: "",
    recognitionRuleId: RecognitionRule.POINT_IN_TIME,
    id,
    standalonePrice: 0,
    recognizedOn: 'startDate'
})

export function computeTermListPrice(product, startDate, endDate) {
    const listPriceAmount = product.listPriceAmount || product.listPrice
    return computeTermAmount(product, startDate, endDate, listPriceAmount)
}

export function computeTermAmount(product, startDate, endDate, amount) {
    if (!product)
        return null;

    const { recurring } = product;

    if (!recurring || !product.listPricePeriodUnit)
        return amount

    const { listPricePeriodUnit, listPricePeriod } = product;

    if (!amount)
        return null;

    const daysPerYear = 365
    const daysPerMonth = new Big(365).div(12)
    const daysPerQuarter = new Big(365).div(4)

    let dayPrice;

    switch (listPricePeriodUnit) {
        case 'years':
            dayPrice = new Big(amount).div(listPricePeriod).div(daysPerYear)
            break;
        case 'months':
            dayPrice = new Big(amount).div(listPricePeriod).div(daysPerMonth)
            break;
        case 'quarters':
            dayPrice = new Big(amount).div(listPricePeriod).div(daysPerQuarter)
            break;
    }

    function termInDays() {

        if (startDate && endDate) {
            const start = moment(startDate, "YYYY-MM-DD");
            const end = moment(endDate, "YYYY-MM-DD");

            if (start.date() == end.date() && start.month() == end.month()) {
                return end.diff(start, 'years') * 365;
            }
            //if you are on the same day/month combo then go ahead and just compute flatly against year

            return end.diff(start, 'days');
            //otherwise compute exact days
        }

        if (product.term) {
            return new Big(product.term).times(daysPerMonth);
        }
        //convert term in months as specified into days

        return daysPerMonth;
        //assume that the term is 1 month if not specified for a recurring product
    }

    const term = termInDays();

    const listPrice =
        dayPrice.times(term).toFixed(4);
    return Number(listPrice);
}

export function populateClientAttributes(clientAttributes, entities, params) {

    clientAttributes && entities && entities.forEach(entity => {

        clientAttributes = clientAttributes.filter(attribute => attribute.entity == entity);

        let attributes = {}

        clientAttributes.forEach(attribute => {
            attributes[attribute.name] = AccountingUtils.evaluateExpression(attribute.expression, params);
        })

        params[entity].attributes = Object.assign({}, params[entity].attributes, attributes);
    })
}

const populateAnnualExtendedSalePrice = (salesOrderItem) => {
    const { startDate, endDate } = salesOrderItem;

    let duration = 12;

    if (startDate && endDate) {
        duration = Utils.getMonthDiff(startDate, endDate, true);
    }

    if (duration == 0) {
        duration = 12
    }

    salesOrderItem.annualExtendedSalePrice = salesOrderItem.extendedSalePrice * (12 / duration);
    salesOrderItem.duration = duration;
}

const getSOI = (salesOrder, itemId) => {
    const soi = salesOrder.salesOrderItem.find(item => {
        const _itemId = item.rootId || item.id
        if (_itemId === itemId) {
            return item
        }
    })

    return soi
}

export const getRevenueArrangementItemBySalesOrderItem = (revenueArrangement, salesOrderItem) => {
    return revenueArrangement && revenueArrangement.revenueArrangementItem.find(rai => {
        let itemId = rai.salesOrderItem.rootId || rai.salesOrderItem.id
        const _itemId = salesOrderItem.rootId || salesOrderItem.id
        if (itemId === _itemId) {
            return rai
        }
    })
}


export function populateDeliveryDates(salesOrder, soi, ssp, rai, errors, customFields, endDateInclusive, dereferenceRuleHasPriority = false) {
    soi.isActive = false

    const lookup = (dateField) => {
        if (dateField.match(/Order Start Date/gi) || dateField.match(/Order Date/gi)) {
            return salesOrder.orderDate;
        }
        //special case for order start date

        let lookupField = dateField;

        const cf =
            customFields && customFields.find((c) => c.displayName === dateField)
        if (cf) {
            lookupField = cf.name;
        }

        if (salesOrder[lookupField]) {
            return salesOrder[lookupField]
        }

        if (salesOrder.orderAttributes) {
            return salesOrder.orderAttributes[lookupField]
        }

        return null;
    }

    const productStartDate = () => {
        if (!soi.product || !soi.product.serviceStartDate || !soi.product.serviceStartDate.trim())
            return null;

        const { serviceStartDate, serviceStartOffset, serviceStartOffsetPeriodUnit }
            = soi.product;

        let startDate =
            moment.isDate(serviceStartDate) ? serviceStartDate : lookup(serviceStartDate);

        if (startDate) {
            startDate = moment(startDate, "YYYY-MM-DD").add(serviceStartOffset, serviceStartOffsetPeriodUnit);
            return Utils.formatDate(startDate);
        }

        return null;
    }

    const searchForDateField = (searchField) => {
        if (!searchField || !searchField.trim()
            || searchField === 'manualDate')
            return null;

        return moment.isDate(searchField) ?
            searchField : lookup(searchField);
    }

    const salesOrderDereferenceStartDate = () => searchForDateField(soi.dereferencedStartDate);
    const sspStartDate = () => searchForDateField(ssp && ssp.deliveryStartDate);

    // In case dereference rules needs to take priority over user selected date,
    // We delete the date and attemp to apply dereference rules.
    let manualStartDate = undefined
    if (dereferenceRuleHasPriority && soi.dereferencedStartDate === 'manualDate') {
        manualStartDate = soi.startDate
        delete soi.startDate
        delete soi.dereferencedStartDate
    }

    // We allow overriding the start date on the UI by specifying a de-referencing field.
    // You can also override by providing it in the SSP or the product
    if (soi.dereferencedStartDate !== 'manualDate') {
        // If dereference source for start date is not salesOrder clear it.
        if (soi.dereferencedStartDateSource && soi.dereferencedStartDateSource !== 'salesOrder') {
            soi.dereferencedStartDate = undefined;
        }

        // Always reset dereference start date value as we will recalcualte it.
        delete soi.dereferencedStartDateValue
        delete soi.startDate

        //0: override at the sales order item level if the user has specified a value
        //to use for a de-reference lookup
        if (soi.dereferencedStartDateSource == 'salesOrder' && soi.dereferencedStartDate) {
            soi.startDate = salesOrderDereferenceStartDate()
        }
        //1. override at the SSP or product level if the user
        //has not specified a date
        else if (ssp && ssp.deliveryStartDate) {
            soi.startDate = sspStartDate();
            soi.dereferencedStartDate = ssp.deliveryStartDate;
            soi.dereferencedStartDateSource = 'ssp';
        } else if (soi.product && soi.product.serviceStartDate) {
            soi.startDate = productStartDate();
            soi.dereferencedStartDate = soi.product.serviceStartDate
            soi.dereferencedStartDateSource = 'product';
        } else {
            soi.dereferencedStartDateValue = undefined
            soi.dereferencedStartDateSource = undefined
        }
    }

    // Unable to apply dereference rules, let's default to the manual date that was set as part of upload
    // or from UI
    if (dereferenceRuleHasPriority && !soi.startDate && manualStartDate) {
        soi.startDate = manualStartDate
        soi.dereferencedStartDate = 'manualDate'
    }

    if (soi.startDate) {
        // We were able to get a good start date
        soi.isActive = true
        soi.dereferencedStartDateValue = soi.startDate
    }

    if (!soi.startDate) {
        //we set a default date here as no date here causes a publishing issuer or something
        //note that the order itself is inactive.
        soi.startDate = salesOrder.orderDate
    }

    const productEndDate = () => {
        if (!soi.product || !soi.product.serviceEndDate || !soi.product.serviceEndDate.trim())
            return null;

        const { serviceEndDate, serviceEndOffset, serviceEndOffsetPeriodUnit }
            = soi.product;

        let endDate =
            moment.isDate(serviceEndDate) ? serviceEndDate : lookup(serviceEndDate);

        if (endDate) {
            if (serviceEndOffset) {
                endDate = moment(endDate, "YYYY-MM-DD").add(serviceEndOffset, serviceEndOffsetPeriodUnit);
            }

            return Utils.formatDate(endDate);
        }

        return null;
    }
    const salesOrderDereferenceEndDate = () => searchForDateField(soi.dereferencedEndDate);
    const sspEndDate = () => searchForDateField(ssp && ssp.deliveryEndDate);

    // In case dereference rules needs to take priority over user selected date,
    // We delete the date and attempt to apply dereference rules.
    let manualEndDate = undefined
    let userSelectedEndDate = undefined
    if (dereferenceRuleHasPriority && soi.dereferencedEndDate === 'manualDate') {
        manualEndDate = soi.endDate
        userSelectedEndDate = soi.userSelectedEndDate
        delete soi.endDate
        delete soi.dereferencedEndDate
        delete soi.userSelectedEndDate
    }

    if (soi.dereferencedEndDate !== 'manualDate') {
        // If dereference source for end date is salesOrder that can't clear it.
        // dont clear for other source, as we will recalculate it.
        if (soi.dereferencedEndDateSource && soi.dereferencedEndDateSource !== 'salesOrder')
            soi.dereferencedEndDate = undefined;

        // Always reset dereference end date value as we will recalcualte it.
        delete soi.dereferencedEndDateValue
        delete soi.endDate

        //0: override at the sales order item level if the user has specified a value
        //to use for a de-reference lookup
        if (soi.dereferencedEndDateSource == 'salesOrder') {
            soi.endDate = salesOrderDereferenceEndDate()
        } else if (ssp && ssp.deliveryEndDate) {
            //1. override at the SSP or product level if the user
            //has not specified a date
            soi.endDate = sspEndDate();
            soi.dereferencedEndDate = ssp.deliveryEndDate;
            soi.dereferencedEndDateSource = 'ssp';
        } else if (soi.product && soi.product.serviceEndDate) {
            soi.endDate = productEndDate();
            soi.dereferencedEndDate = soi.product.serviceEndDate
            soi.dereferencedEndDateSource = 'product';
        } else {
            soi.dereferencedEndDateValue = undefined
            soi.dereferencedEndDateSource = undefined
        }

        if (soi.endDate) {
            soi.dereferencedEndDateValue = soi.endDate

        }
    }

    // derefence end date is manual
    if (soi.startDate && !soi.userSelectedEndDate) {

        if (soi.termSource != 'salesOrder')
            delete soi.term

        let term

        if (soi.term != undefined && soi.termSource == 'salesOrder') {

            // Term in sales order takes priority over dereferenced end date in product and ssp.
            if (!soi.dereferencedEndDateSource || ['product', 'ssp'].includes(soi.dereferencedEndDateSource)) {
                term = soi.term
                soi.termSource = 'salesOrder'
            }

        } else if (ssp.term != undefined && ssp.term) {

            // Term in SSP takes priority over dereferenced end date in product.
            if (!soi.dereferencedEndDateSource || soi.dereferencedEndDateSource == 'product') {
                term = ssp.term
                soi.termSource = 'ssp'
            }
        } else if (soi.product.term != undefined && soi.product.term) {

            if (!soi.dereferencedEndDateSource) {
                term = soi.product.term
                soi.termSource = 'product'
            }
        }

        if (term && soi.isActive) {
            soi.term = term

            let endDate = Utils.addMonthsAsDuration(soi.startDate, soi.term);

            if (endDateInclusive)
                endDate = Utils.addDays(endDate, -1);

            soi.endDate = Utils.formatDate(endDate);

            delete soi.dereferencedEndDateValue
            delete soi.dereferencedEndDateSource
            delete soi.dereferencedEndDate
        }
    }

    // Unable to apply dereference rules, let's default to the manual date that was set as part of upload
    // or from UI
    if (dereferenceRuleHasPriority && !soi.endDate && manualEndDate) {
        soi.endDate = manualEndDate
        soi.dereferencedEndDate = 'manualDate'
        soi.userSelectedEndDate = userSelectedEndDate
    }

    if (!soi.endDate) {
        // If recognition rule is POINT_IN_TIME than enddate is not required.
        // Otherwise mark the item inactive.
        if (ssp.recognitionRuleId !== RecognitionRule.POINT_IN_TIME) {
            soi.isActive = false
        }

        // if null endDate just delete it.
        delete soi.endDate
    }

    // If end date was explicitly selected by user and start date is after end date make end date equal start date.
    // This can happen when user has dereferenced end date for which the value is greater than user selected end date.
    if (soi.userSelectedEndDate
        && soi.endDate
        && soi.startDate
        && Period.isBefore(soi.endDate, soi.startDate)
    ) {
        soi.endDate = soi.startDate
    }

    if (rai) {
        rai.isActive = soi.isActive
        rai.deliveryStartDate = soi.startDate
        rai.deliveryEndDate = soi.endDate || soi.startDate
        rai.billingDate = soi.billingDate

        if (soi.transactionDate) {
            rai.transactionDate = soi.transactionDate
        }
    }
}

function removeRevenueArrangementsInOpenPeriod(salesOrder, organization, calendarConfig, recognizeByTransactionDate) {
    // dont worry about this if there is no close period.
    if (!organization || !salesOrder.revenueArrangement) return;

    let { isRestatementMode } = organization

    let currentPeriod = organization.currentPeriod

    if (isRestatementMode) {
        currentPeriod = '200001'
    }

    const currentClockDate = Period.currentDatePeriod()

    if (recognizeByTransactionDate && Period.isBefore(currentPeriod, currentClockDate)) {
        // recognize by transaction date is enabled, we will use current system clock
        currentPeriod = currentClockDate
    }

    const revenueArrangements = []
    const revenueArrangementIds = []
    const deletedRevenueArrangementIds = []
    salesOrder.revenueArrangement.forEach(revenueArrangement => {
        const raiEffectiveActgPeriod = Period.toActgPeriod(revenueArrangement.effectiveDate, calendarConfig);
        const effectivePeriod = raiEffectiveActgPeriod.period
        // Leave all except for closed period
        if (effectivePeriod < currentPeriod || revenueArrangement.state === 'New') {
            revenueArrangements.push(revenueArrangement)
            revenueArrangementIds.push(revenueArrangement.id)
        } else {
            deletedRevenueArrangementIds.push(revenueArrangement.id)
            if (revenueArrangements.length > 0) {
                let previousRevenueArrangement = revenueArrangements[revenueArrangements.length - 1]

                previousRevenueArrangement.state = 'Current';
                delete previousRevenueArrangement.endDate
            }
        }
    })

    const currentRaId = salesOrder.currentRevenueArrangement && salesOrder.currentRevenueArrangement.id

    if (deletedRevenueArrangementIds.length > 0) {
        salesOrder.deletedRevenueArrangementIds = deletedRevenueArrangementIds;
        salesOrder.revenueArrangement = revenueArrangements;
        salesOrder.revenueArrangementIds = revenueArrangementIds;
    }

    salesOrder.historicalArrangements = {}
    for (let { id, effectiveDate } of revenueArrangements) {
        salesOrder.historicalArrangements[effectiveDate] = id
    }

    if (deletedRevenueArrangementIds.includes(currentRaId)) {
        delete salesOrder.currentRevenueArrangement
    }
}



export function populateRevenueArrangements(salesOrder, clientReferenceData, errors) {

    let { options, journalAccounts, clientAttributes, organization, orgConfig } = clientReferenceData

    // By default create the revenue plans for all sales items.
    options = Object.assign({
        createRevenuePlan: true
    }, options)

    const d = orgConfig.find(config => config.id === 'properties/calendar')
    const calendarConfig = d && d.value && d.value.config;

    const unearnedByConfig = orgConfig.find(config => config.id === 'properties/unearned_by')
    const unearnedByValue = unearnedByConfig && unearnedByConfig.value || 'customer'

    const recognizeByTransactionDateConfig = orgConfig.find(config => config.id === "properties/recognize-by-transaction-date")
    const recognizeByTransactionDate = recognizeByTransactionDateConfig && recognizeByTransactionDateConfig.value

    // no sales order item yet, so bbye.
    if (!salesOrder.salesOrderItem || salesOrder.salesOrderItem.length === 0) {
        return;
    }

    const orgConfigByKey = Utils.getOrgConfigByKey(orgConfig, false)

    if (!salesOrder.revenueArrangement)
        salesOrder.revenueArrangement = [];

    const revenueArrangements = salesOrder.revenueArrangement;

    salesOrder.newRevenueArrangement = revenueArrangements && revenueArrangements.find(revenueArrangement => revenueArrangement.state === 'New');
    salesOrder.currentRevenueArrangement = revenueArrangements.find(revenueArrangement => revenueArrangement.state === 'Current');

    if ((salesOrder.orderType === 'Modify' || salesOrder.orderType === 'New' || salesOrder.orderType === 'Validation Error')) {
        removeRevenueArrangementsInOpenPeriod(salesOrder, organization, calendarConfig, recognizeByTransactionDate);
        if (!salesOrder.newRevenueArrangement) {
            newRevenueArrangement(salesOrder, recognizeByTransactionDate);
        }
    }

    let _period = (organization && organization.currentPeriod) || '200101'
    const currentClockPeriod = Period.currentDatePeriod()

    if (recognizeByTransactionDate && salesOrder.orderType === 'Modify' && Period.isBefore(_period, currentClockPeriod)) {
        // when recognize by transaction date is enabled, we will use current system clock date
        _period = currentClockPeriod
    }

    const orgPeriod = Period.toActgPeriod(_period, calendarConfig)
    let nextArrangementEffectiveDate = orgPeriod.endDate
    //start off assuming it is the end of the current open period

    if (salesOrder.newRevenueArrangement) {

        if (Period.isAfter(salesOrder.orderDate, nextArrangementEffectiveDate)) {
            // order is in the future, revenue should also be in future
            nextArrangementEffectiveDate = salesOrder.orderDate

            if (recognizeByTransactionDate) {
                let _transactionDate = getMinTransactionDate(salesOrder)
        
                if (salesOrder.orderType !== 'Modify' && (_transactionDate && Period.isBefore(nextArrangementEffectiveDate, _transactionDate))) {
                    nextArrangementEffectiveDate = _transactionDate
                } else if (salesOrder.orderType == 'Modify') {
                    // recognize by transaction date is enabled, we will use first of the month to compare
                    // with the transaction date, if the max transaction date is higher than start of current system clock date
                    // then we will use start of month clock for new arrangement date.
                    const startOfCurrentMonth = Period.toStartOfMonth()
                    const maxTransactionDate = getMaxTransactionDate(salesOrder)
        
                    if (maxTransactionDate >= startOfCurrentMonth) {
                        // this should always be the end date of the month
                        // comparision would always be with the start of the month.
                        nextArrangementEffectiveDate = startOfCurrentMonth
                    }
                }
            }
        }

        if (salesOrder.revenueStartDate && Period.isAfter(salesOrder.revenueStartDate, nextArrangementEffectiveDate)) {
            // revenue start date is in the future
            nextArrangementEffectiveDate = salesOrder.revenueStartDate
        }

        salesOrder.newRevenueArrangement.effectiveDate = nextArrangementEffectiveDate
        salesOrder.newRevenueArrangement.postingDate = nextArrangementEffectiveDate
    }

    // Add a rev arrangement item for all new salesOrderItems.
    salesOrder.newRevenueArrangement && salesOrder.salesOrderItem.forEach(soItem => {
        let raItem = salesOrder.newRevenueArrangement.revenueArrangementItem.find(raItem => raItem.salesOrderItemId === soItem.id)
        if (!raItem) {
            salesOrder.newRevenueArrangement.revenueArrangementItem.push(newRevenueArrangementItem(soItem, salesOrder.newRevenueArrangement.id));
        }
    });

    // No arrangements yet.
    if (salesOrder.revenueArrangement.length === 0) return;

    let foundSSP = true

    Utils.resetRuntime(salesOrder);

    try {
        salesOrder.revenueArrangement.forEach(
            (revenueArrangement, raIndex) => {

                let foundNonZeroSSP = false

                revenueArrangement.revenueArrangementItem.forEach(item => {
                    let { salesOrderItem } = item;

                    item.effectiveDate = revenueArrangement.effectiveDate;
                    item.endDate = revenueArrangement.endDate;

                    // Populate custom client attribute;
                    populateClientAttributes(clientAttributes, ['revenueArrangementItem'], {
                        revenueArrangementItem: item,
                        salesOrder
                    });

                    // SSP is already loaded from snapshot.
                    if (!item.ssp) {
                        selectSSP(clientReferenceData, item, salesOrder);

                        // Client has explicitly set standaone selling price of this item.
                        // So so standalonePrice gets overridden.
                        if (item.ssp && (item.salesOrderItem.standaloneSellingPrice || item.salesOrderItem.isDeleted)) {
                            let override_ssp = Utils.copy(item.ssp);
                            override_ssp.id = shortid.generate()
                            override_ssp.standalonePrice = item.salesOrderItem.isDeleted === true ? 0 : item.salesOrderItem.standaloneSellingPrice
                            item.ssp = override_ssp
                        }

                        if (!item.ssp && item.salesOrderItem.standaloneSellingPrice) {
                            const override_ssp = Utils.copy(defaultSSPForProductCode(shortid.generate()))
                            override_ssp.attributes['productCode'] = "TERMINATE";

                            if (item.salesOrderItem.product.recurring == true) {
                                override_ssp.recognitionRuleId = RecognitionRule.RATABLE
                                delete override_ssp.recognizedOn
                            }

                            override_ssp.standalonePrice = item.salesOrderItem.standaloneSellingPrice
                            override_ssp.sspType = 'Revenue'

                            item.ssp = override_ssp
                        }


                        const autoCreateSSP = getAutoCreateSSP(orgConfig)

                        if (!item.ssp && autoCreateSSP == true) {

                            const { fetchBusinessKeyValues } = require('./SalesOrderVisitor')

                            let sspFieldsConfig = orgConfig.find(cfg => cfg.id === 'properties/ssp-fields')
                            let sspFields = (sspFieldsConfig && sspFieldsConfig.value) || []
                            const sspKeyBuilder = Utils.createCompositeKey(sspFields);

                            const missingSSP = fetchBusinessKeyValues(salesOrder, salesOrderItem, sspFields)

                            const override_ssp = Utils.copy(defaultSSPForProductCode(shortid.generate(), missingSSP))
                            override_ssp.id = Utils.searchValue(sspKeyBuilder(missingSSP))

                            override_ssp.standalonePrice = { "type": "AS_IS" }

                            if (AccountingUtils.isDiscountProduct(
                                item.salesOrderItem.product)) {
                                override_ssp.recognitionRuleId = RecognitionRule.POINT_IN_TIME
                                override_ssp.standalonePrice = 0
                            } else {

                                if (item.salesOrderItem.product.recurring == true) {
                                    override_ssp.recognitionRuleId = RecognitionRule.RATABLE
                                    delete override_ssp.recognizedOn
                                }
                            }

                            override_ssp.sspType = 'Revenue'

                            item.ssp = override_ssp

                            // Save the auto created SSP to be saved later.
                            if (!salesOrder.autoCreatedSSP)
                                salesOrder.autoCreatedSSP = {}
                            salesOrder.autoCreatedSSP[item.ssp.id] = item.ssp
                        }
                    }

                    if (!item.ssp) {
                        errors.add(Codes.TRAN_11, { salesOrder, salesOrderItem, revenueArrangementItem: item })
                        foundSSP = false
                    }

                    const soi = getSOI(salesOrder, salesOrderItem.rootId || salesOrderItem.id)

                    if (item.ssp
                        // && (!item.deliveryStartDate || soi.startDate !== item.deliveryStartDate)
                        && (item.isActive == undefined || item.isActive == false)) {

                        const cfConfig = orgConfig.find(config => config.id == 'customFields/attributeMapping');
                        const cFields = cfConfig && cfConfig.value && Object.values(cfConfig.value);

                        const d = orgConfig.find(config => config.id == "properties/ratable_end_date_inclusive")
                        const endDateInclusive = d && d.value || false;

                        const d1 = orgConfig.find(config => config.id == "properties/dereference_rule_has_priority")
                        const dereferenceRuleHasPriority = d1 && d1.value || false;

                        populateDeliveryDates(salesOrder, soi, item.ssp, item, errors,
                            cfConfig && cFields, endDateInclusive, dereferenceRuleHasPriority)

                    }

                    soi.listPrice =
                        Utils.isNumber(soi.listPrice) ? soi.listPrice : computeTermListPrice(soi.product, soi.startDate, soi.endDate)
                    soi.extendedListPrice = soi.quantity * soi.listPrice;
                    // Populate listPrice and extended listPrice

                    populateAnnualExtendedSalePrice(soi);
                    //annual-ize sales price

                    const _soi = getSOI(salesOrder, salesOrderItem.rootId || salesOrderItem.id)
                    if (item.ssp && !_soi.isActive) {
                        const customFieldsConfig = orgConfig.find(config => config.id === 'customFields/attributeMapping');
                        errors.add(Codes.TRAN_30, {
                            salesOrder,
                            salesOrderItem: _soi,
                            customFields: customFieldsConfig && customFieldsConfig.value
                        })
                    }

                    if (item.ssp && item.ssp.recognitionRuleId !== RecognitionRule.POINT_IN_TIME) {
                        const soi = getSOI(salesOrder, salesOrderItem.rootId || salesOrderItem.id)

                        if (soi.endDate && soi.startDate) {

                            let duration = Utils.getMonthDiff(soi.startDate, soi.endDate, false);
                            const d3 = orgConfig.find(cfg => cfg.id === 'properties/max-contract-years')
                            const maxContractYears = (d3 && d3.value && Number(d3.value)) || 15

                            if (duration > maxContractYears * 12) {
                                errors && errors.add(Codes.TRAN_15, { salesOrder, salesOrderItem, maxContractYears });
                            } else if (duration < 0) {
                                errors && errors.add(Codes.TRAN_18, { salesOrder, salesOrderItem });
                            }
                        }
                    }
                })

                if (foundSSP) {
                    let totalESP = 0
                    let standaloneESP = 0

                    let sspType = item => (item.ssp.standalonePrice && typeof item.ssp.standalonePrice === 'object' && item.ssp.standalonePrice.type == 'RESIDUAL') ? 'RESIDUAL' : 'NON_RESIDUAL';

                    revenueArrangement.revenueArrangementItem.sort(
                        Comparator.getComparator(
                            [sspType],
                            [Comparator.forType('string')],
                            false
                        )
                    );

                    revenueArrangement.revenueArrangementItem.forEach(item => {
                        let { salesOrderItem } = item;

                        if (Utils.isEmpty(item.estimatedSalePrice)) {
                            item.estimatedSalePrice = getStandAlonePrice(item, revenueArrangement, salesOrder);
                        }

                        if (Utils.isNumber(item.estimatedSalePrice) && item.estimatedSalePrice != 0 && (item.ssp.standalonePrice && item.ssp.standalonePrice.type !== "STANDALONE")) {
                            foundNonZeroSSP = true
                        }

                        if (Utils.isEmpty(item.totalBooking)) {
                            item.totalBooking = salesOrder.totalBooking;
                        }

                        item.actualSalePrice = salesOrderItem.actualSalePrice
                        item.extendedSalePrice = salesOrderItem.extendedSalePrice

                        item.extendedEstimatedSalePrice = item.estimatedSalePrice * salesOrderItem.quantity

                        if (item.ssp.standalonePrice && item.ssp.standalonePrice.type == "STANDALONE") {
                            // We separated STANDALONE esp thkning about the significant financing component.
                            // That should not have been handled with STANDALONE.
                            standaloneESP += item.extendedEstimatedSalePrice
                        } else {
                            totalESP += item.extendedEstimatedSalePrice;
                        }

                        if (!item.recognitionRuleId == RecognitionRule.PERCENT_OF_COMPLETE
                            && Utils.isEmpty(salesOrderItem.estimatedTotalCost)
                        ) {
                            errors.add(Codes.TRAN_11, { salesOrder, salesOrderItem, revenueArrangementItem: item })
                            foundSSP = false
                        }
                    })

                    revenueArrangement.revenueArrangementItem.forEach((item, raiIndex) => {
                        item.deliveryStartDate = item.deliveryStartDate && formatDate(item.deliveryStartDate);
                        item.deliveryEndDate = item.deliveryEndDate && formatDate(item.deliveryEndDate);

                        item.goLiveDate = item.goLiveDate && formatDate(item.goLiveDate);

                        if (!item.deliveryStartDate && item.goLiveDate) {
                            item.deliveryStartDate = item.goLiveDate;
                        } else if (item.deliveryStartDate && !item.goLiveDate) {
                            item.goLiveDate = item.deliveryStartDate;
                        }

                        if (!foundNonZeroSSP) {
                            item.productRevenue = item.extendedSalePrice
                        } else if (item.ssp.standalonePrice && item.ssp.standalonePrice.type == "STANDALONE") {
                            // We separated STANDALONE esp thinking about the significant financing component.
                            // That should not have been handled with STANDALONE.
                            item.productRevenue = item.extendedEstimatedSalePrice
                        } else {
                            let relativeSSPProportion = (item.extendedEstimatedSalePrice / totalESP) || 0
                            item.productRevenue = relativeSSPProportion * (item.totalBooking - standaloneESP)
                        }

                        item.relativeSalePricePercent = (item.productRevenue / item.totalBooking) || 0

                        // If totalSSP nets to Zero but the items are non zero
                        if (foundNonZeroSSP && totalESP == 0) {
                            item.productRevenue = item.extendedEstimatedSalePrice
                        }

                        if (!item.recognitionRule && item.ssp && item.ssp.recognitionRuleId) {
                            const ruleKey = Object.keys(RecognitionRule).find((ruleKey) => {
                                const value = RecognitionRule[ruleKey];
                                if (value == item.ssp.recognitionRuleId) {
                                    return true;
                                }
                            });

                            item.recognitionRule = { id: item.ssp.recognitionRuleId, name: ruleKey };

                            item.recognitionRuleId = item.ssp.recognitionRuleId;
                        }

                        if (options.createRevenuePlan
                            && ((revenueArrangement.state == 'New'
                                && item.recognitionRule
                                && !item.hasManualUpdates) || (
                                    !item.plan || item.plan.length == 0
                                ))) {
                            buildDefaultRevenuePlan(item, salesOrder, orgConfig)
                        }

                        if (item.plan &&
                            (item.recognitionRule.id === RecognitionRule.PROPORTIONAL_PERFORMANCE
                                || item.recognitionRule.id === RecognitionRule.PERCENT_OF_COMPLETE)
                        ) {
                            // Taking backup of original Plan
                            if (!item.originalPlan) {
                                item.originalPlan = Utils.copy(item.plan)
                            }

                            const enableBetaFeatures = (
                                orgConfigByKey['properties/enableBetaFeatures']
                                && orgConfigByKey['properties/enableBetaFeatures'].value
                            ) || false

                            const incrementalPercentComplete = (
                                orgConfigByKey['properties/service-delivery/percentage-incremental']
                                && orgConfigByKey['properties/service-delivery/percentage-incremental'].value
                            ) || false

                            PSHelper.updatePSPlan(item, salesOrder, errors, enableBetaFeatures, calendarConfig, incrementalPercentComplete);
                        }

                        if (item.plan && unearnedByValue !== 'customer') {
                            addBillingPeriodsToPlan(item.plan, salesOrder, calendarConfig)
                        }

                    })
                }
            }
        )

        //populate the annual booking amount for the order.
        salesOrder.totalAnnualBooking =
            salesOrder.salesOrderItem.reduce((booking, salesOrderItem) => booking += salesOrderItem.annualExtendedSalePrice, 0.0);

        // Populate custom client attribute;
        populateClientAttributes(clientAttributes, ['salesOrder'], { salesOrder });

        ExpenseArrangementHelper.processExpense(clientReferenceData, salesOrder, errors, orgConfig, options)

    } finally {
        Utils.disposeRuntime(salesOrder);
    }

}

const snapshotKeys = ['salesOrderItem', 'ssp', 'attributes', 'estimatedSalePrice', 'totalBooking']

// Restore saved snapshot for revenue arrangement item, if available.
const restoreItemSnapshots = (revenueArrangementItem) => {
    let _snapshotKeys = ['salesOrderItem', 'attributes', 'estimatedSalePrice', 'totalBooking']
    if (revenueArrangementItem.snapshots) {
        _snapshotKeys = snapshotKeys
    }

    // Clear all prior snapshots.
    _snapshotKeys.forEach(key => {
        delete revenueArrangementItem[key]
    });

    revenueArrangementItem.snapshots && Object.keys(revenueArrangementItem.snapshots).forEach(
        key => {
            if (snapshotKeys.includes(key)) {
                revenueArrangementItem[key] = revenueArrangementItem.snapshots[key]
            }
        }
    )
}

export function _nextVersion(salesOrderItemId) {
    var myRegexp = /_v([\d]+)$/;
    var match = myRegexp.exec(salesOrderItemId);
    var priorVersion = (match && match[1]) || 0;
    return Number(priorVersion) + 1;
}

export function getSalesOrderItemRootId(salesOrderItemId) {
    let _myRegexp = /_v([\d]+)$/;
    return salesOrderItemId.replace(_myRegexp, "")
}

export const createSnapshotsForCurrentArrangement = (salesOrder) => {
    return;
}

const getMaxTransactionDate = (salesOrder) => {
    let maxTransactionDate

    salesOrder.salesOrderItem.forEach(item => {
        if (!maxTransactionDate) {
            maxTransactionDate = item.transactionDate
        } else if (item.transactionDate > maxTransactionDate) {
            maxTransactionDate = item.transactionDate
        }
    })

    return maxTransactionDate
}

const getMinTransactionDate = (salesOrder) => {
    let minTransactionDate = salesOrder.salesOrderItem[0].transactionDate

    salesOrder.salesOrderItem.forEach(item => {
        if (!minTransactionDate) {
            minTransactionDate = item.transactionDate
        } else if (item.transactionDate < minTransactionDate) {
            minTransactionDate = item.transactionDate
        }
    })

    return minTransactionDate
}

export const newRevenueArrangement = (salesOrder, recognizeByTransactionDate) => {
    const orderDate = Utils.formatDate(salesOrder.orderDate);

    let revenueStartDate = salesOrder.revenueStartDate != undefined
        ? Utils.formatDate(salesOrder.revenueStartDate)
        : orderDate
    // setting revenueStartDate to salesOrder.revenueStartDate otherwaise defaulting it to orderDate

    let revenueArrangement = {}
    revenueArrangement.effectiveDate = revenueStartDate
    revenueArrangement.isRevenueDelayed = salesOrder.isRevenueDelayed
    revenueArrangement.salesOrderId = salesOrder.id
    revenueArrangement.id = shortid.generate();
    revenueArrangement.new = true;
    revenueArrangement.revenueArrangementItem = salesOrder.salesOrderItem.map((item, index) => {
        return newRevenueArrangementItem(item, revenueArrangement.id);
    })
    revenueArrangement.state = 'New';

    if (salesOrder.orderType == 'Modify') {
        revenueArrangement.modificationType = ModificationType.RETROSPECTIVE
    }

    if (salesOrder.expenseItems && salesOrder.expenseItems.length) {
        revenueArrangement.expenseItems = Utils.cloneDeep(salesOrder.expenseItems)
    } else {
        revenueArrangement.expenseItems = []
    }

    salesOrder.revenueArrangement.push(revenueArrangement);
    if (!salesOrder.revenueArrangementIds) {
        salesOrder.revenueArrangementIds = []
    }
    salesOrder.revenueArrangementIds.push(revenueArrangement.id)
    revenueArrangement.jobId = salesOrder.jobId
    salesOrder.newRevenueArrangement = revenueArrangement;
}

export const newRevenueArrangementItem = (salesOrderItem, revenueArrangementId) => {

    return {
        id: shortid.generate(),
        salesOrderItemId: salesOrderItem.id,
        salesOrderItem: salesOrderItem,
        expenseItems: [],
        revenueArrangementId: revenueArrangementId,
        liabilityAccountId: salesOrderItem.deferredRevenueAccountNumber,
        revenueAccountId: salesOrderItem.revenueAccountNumber,
        contraRevenueAccountId: salesOrderItem.contraRevenueAccountNumber,
        deferredCommissionAccountId: salesOrderItem.deferredCommissionAccountNumber,
        commissionAmortizationAccountId: salesOrderItem.commissionAccountNumber,
        contraCommissionExpenseAccountId: salesOrderItem.contraCommissionExpenseAccountNumber,
        commissionAccumulatedAmortizationAccountId: salesOrderItem.commissionAccumulatedAmortizationAccountNumber
    }
}

/* -------
   Default Revenue Plan
   -------  */
export function buildDefaultRevenuePlan(revenueArrangementItem, salesOrder, orgConfig) {
    if (!revenueArrangementItem || !revenueArrangementItem.recognitionRule)
        throw new Error("Missing revenue arrangement item or rule");

    const d = orgConfig.find(config => config.id === 'properties/calendar')
    const calendarConfig = d && d.value && d.value.config;

    let productRevenue = revenueArrangementItem.productRevenue

    if (salesOrder.revenueArrangement) {
        const currentRevenueArrangement = salesOrder.revenueArrangement.find(ra => ra.id == revenueArrangementItem.revenueArrangementId)

        if (currentRevenueArrangement.isRevenueDelayed) {
            productRevenue = 0
        }
    }

    switch (revenueArrangementItem.recognitionRule.id) {
        case RecognitionRule.POINT_IN_TIME:
            revenueArrangementItem.plan = PointInTimePlan.buildPlan(
                revenueArrangementItem.deliveryStartDate,
                revenueArrangementItem.deliveryEndDate,
                productRevenue,
                revenueArrangementItem.ssp,
                calendarConfig)
            break;
        case RecognitionRule.PROPORTIONAL_PERFORMANCE:
        case RecognitionRule.PERCENT_OF_COMPLETE:
            const plan = buildPlan(revenueArrangementItem, salesOrder, orgConfig, productRevenue, calendarConfig);
            PSHelper.updateToBaseServicePlan(plan, productRevenue)
            revenueArrangementItem.plan = plan
            break;
        default:
            revenueArrangementItem.plan = buildPlan(revenueArrangementItem, salesOrder, orgConfig, productRevenue, calendarConfig);
    }

    prefillZeroAtStart(revenueArrangementItem.plan, salesOrder, calendarConfig)
}

const getMaxBillingDate = (salesOrder) => {
    let maxBillingDate
    // pick max billing date
    if (salesOrder.billingSchedule && salesOrder.billingSchedule.billingScheduleItem) {
        for (const bsi of salesOrder.billingSchedule.billingScheduleItem) {
            const schedule = bsi.schedule
            if (Array.isArray(schedule) && schedule.length) {
                const sortedByBillingDate = _.sortBy(schedule, s => s.billingDate)
                const lastBilling = sortedByBillingDate[sortedByBillingDate.length - 1]
                const lastBillingDate = lastBilling.billingDate

                if (!maxBillingDate) {
                    maxBillingDate = lastBillingDate
                } else if (lastBillingDate > maxBillingDate) {
                    maxBillingDate = lastBillingDate
                }
            }
        }
    }

    return maxBillingDate
}

/**
 * If we have billing that is coming after the effective date of the
 * revenue arrangement then we want to add those periods in the plan
 * with 0 so that when transactions are generated they don't have any missing
 * rows in it
 * @param {*} plan 
 * @param {*} salesOrder 
 * @param {*} calendarConfig 
 * @returns 
 */

export function addBillingPeriodsToPlan(plan, salesOrder, calendarConfig) {

    if (plan.length == 0) return

    const maxBillingDate = getMaxBillingDate(salesOrder)

    if (!maxBillingDate) return

    const lastPlanActgPeriod = plan[plan.length - 1].actgPeriod;

    const maxBillingEffectivePeriod =
        Period.toActgPeriod(maxBillingDate, calendarConfig);

    if (maxBillingEffectivePeriod.period <= lastPlanActgPeriod.period)
        return;

    const periods =
        Period.periodsBetween(lastPlanActgPeriod.period, maxBillingEffectivePeriod.period, false, true);

    if (periods.length == 0) return;

    let actgPeriod = lastPlanActgPeriod;
    for (let period of periods) {
        actgPeriod = actgPeriod.get(period);

        plan.push({
            actgPeriod,
            planAmount: 0,
            percentRecognized: 0
        });
    }
}

export function updateQuantityPlan(revenueArrangementItem, plan) {

    let availableQuantity = revenueArrangementItem.salesOrderItem.quantity;
    let originalBooking = revenueArrangementItem.productRevenue;
    let totalBooking = originalBooking
    let unitCost = totalBooking / availableQuantity;

    var last = plan[plan.length - 1]

    plan.forEach(element => {

        if (element.quantity > availableQuantity) {
            element.quantity = availableQuantity
        }

        if (element == last && element.quantity < availableQuantity) {
            element.quantity = availableQuantity
        }

        element.planAmount = unitCost * element.quantity
        element.percentRecognized = element.planAmount / originalBooking

        totalBooking -= element.planAmount
        element.cummulativeTotal = originalBooking - totalBooking;
        element.cummulativePercentRecognized = element.cummulativeTotal / originalBooking;

        availableQuantity -= element.quantity;
    });

}

export function doRACleanup(ra) {

    const cleanupSOI = (soi) => {
        delete soi.revenueAccountNumber
        delete soi.deferredRevenueAccountNumber
        delete soi.contraRevenueAccountNumber
        delete soi.commissionAccountNumber
        delete soi.deferredCommissionAccountNumber
        delete soi.commissionAccumulatedAmortizationAccountNumber
        delete soi.contraCommissionExpenseAccountNumber

        delete soi.product.jobId
        delete soi.product.lastModifiedBy
        delete soi.product.lastModifiedDate
        delete soi.product.rowNum
    }

    const cleanupExpenseItem = (expItem, removePlan = false) => {
        delete expItem.rowNum

        if (removePlan) {
            delete expItem.plan
        }

        if (expItem.expenseRule) {
            delete expItem.expenseRule.xlRow
            delete expItem.expenseRule.lastModifiedBy
            delete expItem.expenseRule.lastModifiedDate
        }

        if (expItem.salesPerson) {
            delete expItem.salesPerson.new
            delete expItem.salesPerson.lastModifiedBy
        }
    }

    for (const rai of ra.revenueArrangementItem) {

        // 1 cleanup rai level keys
        delete rai.liabilityAccountId
        delete rai.revenueAccountId
        delete rai.contraRevenueAccountId
        delete rai.deferredCommissionAccountId
        delete rai.commissionAmortizationAccountId
        delete rai.contraCommissionExpenseAccountId
        delete rai.commissionAccumulatedAmortizationAccountId

        if (rai.ssp) {
            delete rai.ssp.xlRow
            delete rai.ssp.lastModifiedBy
            delete rai.ssp.lastModifiedDate
        }

        // 2. cleanup sales order item level keys
        if (rai.salesOrderItem) {
            cleanupSOI(rai.salesOrderItem)
        }

        // 2. cleanup expense item level keys
        if (rai.expenseItems && rai.expenseItems.length) {
            for (const expItem of rai.expenseItems) {
                cleanupExpenseItem(expItem)
            }
        }

        // 3. cleanup snapshot level keys
        if (rai.snapshots) {
            if (rai.snapshots.ssp) {
                delete rai.snapshots.ssp.xlRow
                delete rai.snapshots.ssp.lastModifiedBy
                delete rai.snapshots.ssp.lastModifiedDate
            }

            if (rai.snapshots.salesOrderItem) {
                cleanupSOI(rai.snapshots.salesOrderItem)
            }

            if (rai.snapshots.expenseItems && rai.snapshots.expenseItems.length) {
                delete rai.snapshots.expenseItems
            }
        }
    }
}